import { useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useUserProfile } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Phone, Search, SearchX, Share2, Users } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { PageHeader } from "@/components/layout/page-header";
import { buttonVariants } from "@/components/ui/button-variants";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/account/provider/patients")({
  component: ProviderPatientsPage,
});

interface PatientHit {
  id: string;
  firstName: string;
  lastName: string;
  /** Masked by the API ("+407***123"). */
  phone: string | null;
}

/** The API answers nothing below 2 characters (no list mode). */
const MIN_QUERY = 2;

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function ProviderPatientsPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const profileQuery = useUserProfile();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query.trim(), 300);
  const hasProvider = profileQuery.data?.hasProviderProfile === true;

  // Soft gate (see reviews.tsx for rationale). Kept: team-seat holders get a
  // 403 from the provider endpoints.
  useEffect(() => {
    if (profileQuery.data && profileQuery.data.hasProviderProfile === false) {
      navigate({
        to: "/account",
        search: { reason: "no-provider-profile" } as never,
        replace: true,
      });
    }
  }, [profileQuery.data, navigate]);

  const searchQ = useQuery({
    queryKey: ["api", "/api/account/provider/patients", debounced],
    queryFn: () =>
      apiFetch.get<{ patients: PatientHit[] }>(
        `/api/account/provider/patients?q=${encodeURIComponent(debounced)}&limit=20`,
      ),
    enabled: hasProvider && debounced.length >= MIN_QUERY,
  });

  const searching = debounced.length >= MIN_QUERY;
  const results = searchQ.data?.patients ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={<Trans>Patients</Trans>}
        description={<Trans>Your patient roster — history, notes, follow-ups.</Trans>}
      />

      <div className="relative">
        <Search
          className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2"
          aria-hidden
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t`Search patients…`}
          aria-label={t`Search patients`}
          className="border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 min-h-[44px] w-full rounded-lg border py-2.5 pr-4 pl-10 text-sm transition-colors focus-visible:ring-3 focus-visible:outline-none"
        />
      </div>

      <div aria-live="polite">
        {!searching ? (
          <StateCard
            icon={Users}
            title={<Trans>Search your patients by name</Trans>}
            body={
              <Trans>
                Type at least two letters of a first or last name to find a patient who booked with
                you.
              </Trans>
            }
          />
        ) : searchQ.isPending ? (
          <ul className="space-y-2">
            {[0, 1, 2].map((i) => (
              <li key={i}>
                <Skeleton className="h-16 w-full rounded-xl" />
              </li>
            ))}
          </ul>
        ) : searchQ.isError ? (
          <LoadErrorState
            title={<Trans>Could not search your patients.</Trans>}
            onRetry={() => void searchQ.refetch()}
            retrying={searchQ.isFetching}
          />
        ) : results.length === 0 ? (
          <StateCard
            icon={SearchX}
            title={<Trans>No patients match</Trans>}
            body={
              <Trans>
                Patients appear here once they book a consultation with you. Share your booking link
                to get your first bookings.
              </Trans>
            }
            cta={
              <Link
                to="/account/provider/booking-widget"
                className={cn(buttonVariants({ size: "touch" }), "mt-4")}
              >
                <Share2 className="size-4" aria-hidden />
                <Trans>Share your booking link</Trans>
              </Link>
            }
          />
        ) : (
          <ul className="space-y-2">
            {results.map((p) => (
              <li
                key={p.id}
                className="border-border bg-card flex min-h-16 items-center gap-3 rounded-xl border p-3"
              >
                <div className="bg-muted text-foreground flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-bold">
                  {p.firstName?.[0] ?? "?"}
                  {p.lastName?.[0] ?? ""}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-foreground truncate text-sm font-semibold">
                    {`${p.firstName} ${p.lastName}`.trim()}
                  </p>
                  {p.phone ? (
                    <p className="text-muted-foreground inline-flex items-center gap-1 text-xs tabular-nums">
                      <Phone className="size-3" aria-hidden />
                      {p.phone}
                    </p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AccountSecurityBanner />
    </div>
  );
}

function StateCard({
  icon: Icon,
  title,
  body,
  cta,
}: {
  icon: typeof Users;
  title: React.ReactNode;
  body: React.ReactNode;
  cta?: React.ReactNode;
}) {
  return (
    <div className="border-border bg-card flex flex-col items-center justify-center rounded-xl border px-4 py-16 text-center">
      <div className="bg-muted rounded-full p-4">
        <Icon className="text-muted-foreground size-8" aria-hidden />
      </div>
      <p className="text-foreground mt-4 text-sm font-medium">{title}</p>
      <p className="text-muted-foreground mt-1 max-w-sm text-xs">{body}</p>
      {cta}
    </div>
  );
}
